import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BrandLogo from "@/components/marketing/BrandLogo";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [services, setServices] = useState([]);
  const [useCases, setUseCases] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/onboarding/options").then((r) => setOptions(r.data));
  }, []);

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const fetchRec = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/recommend", { industry, services, use_cases: useCases });
      setRecommendation(data);
      setStep(3);
    } catch (err) {
      toast.error("Failed to generate recommendation");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/complete", {
        industry,
        services,
        use_cases: useCases,
        package_id: recommendation.recommended_package.id,
        agent_name: `${options.industries.find((i) => i.id === industry)?.name || "My"} Agent`,
        agent_prompt: recommendation.starter_prompt,
      });
      await refresh();
      toast.success("Workspace ready");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Failed to finish onboarding");
    } finally { setBusy(false); }
  };

  if (!options) return <div className="min-h-screen flex items-center justify-center text-sm text-[#4B5563]">Loading…</div>;

  const steps = ["Industry", "Services", "Use cases", "Review"];

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[#F9FAFB]">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-10">
          <BrandLogo size="md" />
          <div className="flex items-center gap-2">
            {steps.map((s, i) => (
              <div key={s} className={`px-3 py-1 text-xs font-mono ${i === step ? "bg-[#0044FF] text-white" : i < step ? "bg-[#030712] text-white" : "bg-white border border-[#E2E8F0] text-[#4B5563]"}`}>
                {i + 1}. {s}
              </div>
            ))}
          </div>
        </div>

        {step === 0 && (
          <div data-testid="onboarding-step-industry">
            <h1 className="font-display font-extrabold text-4xl">What's your industry?</h1>
            <p className="text-[#4B5563] mt-2">We use this to pre-write your agent's prompt and pick smart defaults.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
              {options.industries.map((ind) => (
                <button
                  key={ind.id}
                  data-testid={`industry-${ind.id}`}
                  onClick={() => setIndustry(ind.id)}
                  className={`hs-card p-5 text-left hover:border-[#0044FF] transition-colors ${industry === ind.id ? "border-[#0044FF] ring-2 ring-[#0044FF]/20" : ""}`}
                >
                  <div className="font-semibold">{ind.name}</div>
                  <div className="text-xs text-[#4B5563] mt-1">{ind.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="onboarding-step-services">
            <h1 className="font-display font-extrabold text-4xl">Which channels do you want?</h1>
            <p className="text-[#4B5563] mt-2">Mix and match — you can change this any time.</p>
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              {options.services.map((s) => {
                const active = services.includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`service-${s.id}`}
                    onClick={() => toggle(services, setServices, s.id)}
                    className={`hs-card p-6 text-left ${active ? "border-[#0044FF] ring-2 ring-[#0044FF]/20" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-lg">{s.name}</div>
                      {active && <Check className="w-5 h-5 text-[#0044FF]" />}
                    </div>
                    <div className="text-sm text-[#4B5563] mt-2">{s.description}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboarding-step-use-cases">
            <h1 className="font-display font-extrabold text-4xl">Pick your use cases</h1>
            <p className="text-[#4B5563] mt-2">Choose all that apply.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
              {options.use_cases.map((u) => {
                const active = useCases.includes(u.id);
                return (
                  <button
                    key={u.id}
                    data-testid={`usecase-${u.id}`}
                    onClick={() => toggle(useCases, setUseCases, u.id)}
                    className={`hs-card p-4 text-left ${active ? "border-[#0044FF] ring-2 ring-[#0044FF]/20" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{u.name}</div>
                      {active && <Check className="w-4 h-4 text-[#0044FF]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && recommendation && (
          <div data-testid="onboarding-step-review" className="grid md:grid-cols-2 gap-6">
            <div>
              <h1 className="font-display font-extrabold text-4xl">Your recommended setup</h1>
              <p className="text-[#4B5563] mt-2">Hit finish and we'll provision your starter agent. <strong>Pay-as-you-go</strong> — no subscription, no surprises.</p>

              <div className="hs-card p-6 mt-6 bg-gradient-to-br from-[#F0F4FF] to-white border-[#0044FF]/20">
                <div className="text-xs font-mono text-[#0044FF] uppercase tracking-widest">free starter</div>
                <div className="font-display font-extrabold text-2xl mt-2">Pay-as-you-go — no card required</div>
                <ul className="text-sm space-y-2 mt-4 text-[#0F1115]">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" />1 agent included</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" />100 Web Chat messages</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" />20 minutes Web Voice</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" />1 website scrape</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" />Full access to all integrations</li>
                </ul>
                <p className="text-[11px] font-mono text-[#4B5563] mt-4">No time limit. When credits run out, the agent simply pauses until you top up.</p>
              </div>

              <div className="hs-card p-6 mt-4">
                <div className="text-xs font-mono text-[#4B5563] uppercase tracking-widest">top-ups when you're ready</div>
                <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                  <div className="px-3 py-2 rounded bg-[#F3F4F6]"><div className="font-bold">$9.99</div><div className="text-[11px] text-[#4B5563]">1,000 Web Chat msgs</div></div>
                  <div className="px-3 py-2 rounded bg-[#F3F4F6]"><div className="font-bold">$9.99</div><div className="text-[11px] text-[#4B5563]">100 Web Voice min</div></div>
                  <div className="px-3 py-2 rounded bg-[#F3F4F6]"><div className="font-bold">$9.99</div><div className="text-[11px] text-[#4B5563]">100 AI Calling min</div></div>
                  <div className="px-3 py-2 rounded bg-[#F3F4F6]"><div className="font-bold">$1.99</div><div className="text-[11px] text-[#4B5563]">Extra website scrape</div></div>
                </div>
                <div className="text-[11px] font-mono text-[#4B5563] mt-3">Credits never expire. Cancel anytime.</div>
              </div>

              <div className="hs-card p-6 mt-4">
                <div className="text-xs font-mono text-[#4B5563]">smart features unlocked</div>
                <ul className="text-sm space-y-1.5 mt-3">
                  {recommendation.recommended_features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#0044FF] mt-0.5" />{f}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <div className="hs-card p-6">
                <div className="text-xs font-mono text-[#4B5563]">starter system prompt</div>
                <pre className="text-xs font-mono whitespace-pre-wrap mt-3 text-[#030712] max-h-[420px] overflow-auto">{recommendation.starter_prompt}</pre>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-10">
          <button
            data-testid="onboarding-back-button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="btn-outline disabled:opacity-40 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 3 ? (
            <button
              data-testid="onboarding-next-button"
              disabled={
                (step === 0 && !industry) ||
                (step === 1 && services.length === 0) ||
                (step === 2 && useCases.length === 0) ||
                busy
              }
              onClick={() => (step === 2 ? fetchRec() : setStep((s) => s + 1))}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {step === 2 ? (busy ? "Thinking…" : "See recommendation") : "Next"} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="onboarding-finish-button"
              onClick={finish}
              disabled={busy}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? "Setting up…" : "Finish & enter dashboard"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
